import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";

interface ResponsiveHeaderProps {
  currentStep: number;
  totalSteps: number;
  onBack?: () => void;
  showProgress: boolean;
  stepTitle?: string;
  stepIcon?: React.ComponentType<{ className?: string }>;
  className?: string;
  steps?: { id: string; title: string }[];
}

export function ResponsiveHeader({
  currentStep,
  totalSteps,
  onBack,
  showProgress,
  stepTitle,
  stepIcon: StepIcon,
  className,
  steps,
}: ResponsiveHeaderProps) {
  const progressPercent = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div className={cn("bg-white shadow-sm border-b", className)}>
      <div className="max-w-md mx-auto lg:max-w-2xl xl:max-w-4xl px-4 py-3 md:px-6 md:py-4 lg:px-10">
        {/* Premium Jema Services Header - Hidden on success step */}
        {currentStep < totalSteps - 1 && (
          <div className="text-center mb-2 pb-4 border-b border-gradient-to-r from-jema-blue/20 to-jema-orange/20">
            <div className="flex flex-col items-center justify-center">
              <img
                src="jema services logo full.svg"
                alt="Jema Services Logo"
                className="max-w-[200px] w-full h-auto object-contain mb-0 leading-none"
                style={{ marginBottom: "-25px" }}
              />
              <div className="leading-tight text-center mt-1">
                <p className="text-sm lg:text-base font-semibold text-jema-orange">
                  Dental Care Without Borders
                </p>
                <p className="text-xs lg:text-sm text-gray-500">
                  Premium Dental Tourism in Colombia
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation and Step Info */}
        <div className="flex items-center justify-between mb-4">
          {onBack && currentStep > 0 && currentStep < totalSteps - 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-1 text-jema-blue hover:text-jema-blue/80 min-w-touch min-h-touch lg:px-4"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          )}
          <div className="flex-1" />
          {currentStep < totalSteps - 1 && (
            <span className="text-sm lg:text-base text-gray-500">
              Step {currentStep + 1} of {totalSteps - 1}
            </span>
          )}
        </div>

        {/* Step Title and Progress */}
        {showProgress && currentStep < totalSteps - 1 && (
          <div className="space-y-3">
            {/* Step Title with Icon */}
            {(stepTitle || StepIcon) && (
              <div className="flex items-center gap-2 lg:gap-3">
                {StepIcon && (
                  <StepIcon className="w-5 h-5 lg:w-6 lg:h-6 text-jema-blue" />
                )}
                {stepTitle && (
                  <h1 className="text-lg lg:text-xl xl:text-2xl font-semibold text-gray-900 font-quicksand">
                    {stepTitle}
                  </h1>
                )}
              </div>
            )}

            {/* Progress Bar */}
            <div className="space-y-2">
              <Progress
                value={progressPercent}
                className="h-2 lg:h-3 bg-gray-100"
                style={
                  {
                    "--progress-foreground":
                      "linear-gradient(90deg, #166B93 0%, #CEF12F 50%, #FF9142 100%)",
                  } as any
                }
              />

              {/* Mobile: Show step indicators */}
              <div className="lg:hidden overflow-x-auto whitespace-nowrap py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex space-x-4 text-xs">
                  {steps?.map((step, index) => (
                    <span
                      key={step.id}
                      className={cn(
                        "pb-1",
                        index === currentStep
                          ? "font-bold text-jema-blue border-b-2 border-jema-blue"
                          : "text-gray-500",
                      )}
                    >
                      {step.title}
                    </span>
                  ))}
                </div>
              </div>

              {/* Desktop: Show step indicators */}
              <div className="hidden lg:flex justify-between text-xs text-gray-500">
                {steps?.map((step) => (
                  <span key={step.id}>{step.title}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
